import { helpWhy } from '../../help/why';
import React, { useEffect, useState } from 'react';
import {
    Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { ExportLcService } from '../../services/exportLc.service';
import { PERMISSIONS } from '../../constants/permissions';
import { CreateLcChecklistItemRequest, LcChecklistItem } from '../../interfaces/commercial.interface';
import { toLocalIsoDate, formatDate } from '../../utils/dateUtils';

const EMPTY = { documentName: '', required: true, remarks: '' };

const LcChecklistPanel: React.FC<{ lcId: string }> = ({ lcId }) => {
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.LC_MANAGE);

    const [rows, setRows] = useState<LcChecklistItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ ...EMPTY });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            setLoading(true);
            const res = await ExportLcService.listChecklist(institutionId, lcId, 0, 200);
            setRows(res.content);
        } catch (e) {
            toast.showApiError(e, 'Failed to load document checklist');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [lcId]); // eslint-disable-line react-hooks/exhaustive-deps

    const saveWhy = saving ? null
        : !form.documentName.trim() ? 'Enter the document name.'
        : null;

    const handleAdd = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId || !form.documentName.trim()) return;
        const dto: CreateLcChecklistItemRequest = {
            documentName: form.documentName.trim(),
            required: form.required,
            remarks: form.remarks.trim() || undefined,
        };
        try {
            setSaving(true);
            await ExportLcService.addChecklistItem(institutionId, lcId, dto);
            toast.showSuccess('Document added');
            setOpen(false);
            setForm({ ...EMPTY });
            load();
        } catch (e) {
            toast.showApiError(e, 'Failed to add document');
        } finally {
            setSaving(false);
        }
    };

    const toggleSubmitted = async (item: LcChecklistItem) => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        const nextSubmitted = !item.submitted;
        try {
            await ExportLcService.updateChecklistItem(institutionId, lcId, item.id, {
                required: item.required,
                submitted: nextSubmitted,
                submittedDate: nextSubmitted ? toLocalIsoDate() : undefined,
                remarks: item.remarks,
            });
            load();
        } catch (e) {
            toast.showApiError(e, 'Failed to update document');
        }
    };

    const handleRemove = async (item: LcChecklistItem) => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            await ExportLcService.removeChecklistItem(institutionId, lcId, item.id);
            toast.showSuccess('Document removed');
            load();
        } catch (e) {
            toast.showApiError(e, 'Failed to remove document');
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6">Document Checklist</Typography>
                {canManage && (
                    <Button data-help="commercial.lcChecklist.add" startIcon={<AddIcon />} variant="outlined" onClick={() => setOpen(true)}>
                        Add Document
                    </Button>
                )}
            </Box>
            {loading ? <CircularProgress /> : (
                <TableContainer component={Paper} data-help-section="commercial.lcChecklist.table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="commercial.lcChecklist.col.document">Document</TableCell>
                                <TableCell data-help="commercial.lcChecklist.col.required">Required</TableCell>
                                <TableCell data-help="commercial.lcChecklist.col.submitted">Submitted</TableCell>
                                <TableCell data-help="commercial.lcChecklist.col.submittedDate">Submitted Date</TableCell>
                                <TableCell data-help="commercial.lcChecklist.col.remarks">Remarks</TableCell>
                                {canManage && <TableCell align="right" data-help="commercial.lcChecklist.col.actions">Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(item => (
                                <TableRow key={item.id}>
                                    <TableCell>{item.documentName}</TableCell>
                                    <TableCell>{item.required ? 'Yes' : 'No'}</TableCell>
                                    <TableCell>
                                        <Checkbox
                                            data-help="commercial.lcChecklist.toggle"
                                            checked={item.submitted}
                                            disabled={!canManage}
                                            onChange={() => toggleSubmitted(item)}
                                        />
                                    </TableCell>
                                    <TableCell>{item.submittedDate ? formatDate(item.submittedDate) : '—'}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'normal', minWidth: 140 }}>{item.remarks ?? '—'}</TableCell>
                                    {canManage && (
                                        <TableCell align="right">
                                            <IconButton data-help="commercial.lcChecklist.remove" size="small" onClick={() => handleRemove(item)} aria-label={`remove-${item.documentName}`}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                            {rows.length === 0 && (
                                <TableRow><TableCell colSpan={canManage ? 6 : 5} align="center">No checklist items yet.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'commercial.lcChecklist.dialog.page' } as any}>
                <DialogTitle>Add Document</DialogTitle>
                <DialogContent>
                    <TextField
                        data-help="commercial.lcChecklist.nameField" label="Document Name" fullWidth required sx={{ mt: 1, mb: 2 }}
                        value={form.documentName}
                        onChange={(e) => setForm(f => ({ ...f, documentName: e.target.value }))}
                    />
                    <FormControlLabel
                        data-help="commercial.lcChecklist.requiredField"
                        control={
                            <Checkbox
                                checked={form.required}
                                onChange={(e) => setForm(f => ({ ...f, required: e.target.checked }))}
                            />
                        }
                        label="Required"
                    />
                    <TextField
                        data-help="commercial.lcChecklist.remarksField" label="Remarks" fullWidth multiline rows={2} sx={{ mt: 2 }}
                        value={form.remarks}
                        onChange={(e) => setForm(f => ({ ...f, remarks: e.target.value }))}
                    />
                </DialogContent>
                <DialogActions>
                    <Button data-help="commercial.lcChecklist.cancel" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button data-help="commercial.lcChecklist.save" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} variant="contained" disabled={saving || !form.documentName.trim()} onClick={handleAdd}>
                        {saving ? 'Saving…' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LcChecklistPanel;
